import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
  modify,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';

type LegalPersonType =
  | 'BUSINESS'
  | 'ORGANIZATION'
  | 'SOLETRADER'
  | 'PARTNERSHIP';

export interface GetServiceProviderResult {
  data: {
    name: string;
    phone?: string | null;
    address?: string | null;
    zip_code?: string | null;
    city?: string | null;
    mangopay_kyc_verified: boolean;
    mangopay_sca_enrolled: boolean;
    mangopay_sca_activated: boolean;
    mangopay_user_id: string | null;
    mangopay_wallet_id?: string | null;
    payout_schedule: 'disabled' | 'daily' | 'weekly';
    mangopay_payout_day_of_week: number | null;
    mangopay_recipient_status?:
      | 'PENDING'
      | 'ACTIVE'
      | 'CANCELED'
      | 'DEACTIVATED'
      | null;
    mangopay_sca_url?: string | null;
    mangopay_bank_account_id?: string | null;
    global_service_provider_id: string;
    legal_person_type: LegalPersonType;
    payment_service_provider: 'mangopay' | 'stripe' | null;
    country_id?: string | null;
    default_tax_code?: string | null;
    default_auto_generate_receipts?: boolean;
    stripe_account?: {
      stripe_account_id: string;
      account_onboarding_status: 'complete' | 'pending';
      owned_by_pflegenavi: boolean;
    } | null;
    mangopay_bank_account?: {
      owner_address: {
        region: string;
        country: string;
        city: string;
        postal_code: string;
        address_line1: string;
        address_line2: string;
      };
      iban: string;
      user_id: string;
      owner_name: string;
      active: boolean;
      creation_date: number;
      id: string;
    };
    nursing_home_connections?: Array<{
      nursing_home_id: string;
      nursing_home_name: string;
      tenant: string;
      connection_status:
        | 'invite_pending'
        | 'active'
        | 'inactive'
        | 'invite_rejected';
      payment_processor?: 'stripe' | 'mangopay';
    }>;
  };
  redirect_url?: string;
}

export interface GetDashboardResult {
  total_balance: number;
  available_balance: number;
  pending_balance: number;
  in_payout_balance: number;
}

export interface GetPayoutsResult {
  data: Array<{
    id: string;
    statement_descriptor: string;
    iban_last_4: string;
    amount: number;
    currency: string;
    state: 'CREATED' | 'SUCCEEDED' | 'FAILED';
    inserted_at: string;
  }>;
  meta: {
    total_count: number;
  };
}

export interface GetPayoutsParams {
  params: {
    page: number;
    pageSize: number;
    state: 'CREATED' | 'SUCCEEDED' | 'FAILED' | undefined;
  };
}

export interface PayoutReconciliationEntry {
  id: string;
  mangopay_resource_id: string;
  mangopay_transaction_type: 'TRANSFER' | 'PAYOUT';
  mangopay_transaction_nature: string;
  amount: number;
  currency: string;
  performed_at: string;
  tenant: string;
  receipt_batch_entry: {
    id: string;
    receipt_date: string;
    batch_sequence_nr: number;
    resident: {
      id: string;
      firstname: string;
      lastname: string;
      gender: string;
    } | null;
    receipt_batch: {
      id: string;
      receipt_date: string;
      accepted_on: string | null;
      state: string;
    } | null;
  } | null;
  nursing_home: {
    nursing_home_id: string;
    nursing_home_name: string;
    tenant: string;
  } | null;
  batch_entry_count: number | null;
  batch_min_receipt_date: string | null;
  batch_max_receipt_date: string | null;
}

export interface GetPayoutReconciliationResult {
  data: PayoutReconciliationEntry[];
}

export interface GetPayoutReconciliationParams {
  params: {
    payoutId: string;
  };
}

export type OpenItemStatus =
  | 'open'
  | 'available_soon'
  | 'available'
  | 'deleted';

export interface OpenItem {
  id: string;
  amount: number;
  receipt_date: Date | null;
  embargo_date: Date | null;
  status: OpenItemStatus;
  tenant: string | null;
  resident: {
    id: string;
    firstname: string;
    lastname: string;
    gender: string;
  } | null;
  nursing_home: {
    nursing_home_id: string;
    nursing_home_name: string;
    tenant: string;
  } | null;
  batch_receipt_date: Date | null;
  batch_use_individual_dates: boolean | null;
  batch_entry_count: number | null;
  batch_min_receipt_date: Date | null;
  batch_max_receipt_date: Date | null;
}

export interface GetOpenItemsResult {
  data: OpenItem[];
}

export interface UpdateServiceProviderParams {
  name?: string;
  phone?: string;
  address?: string;
  zip_code?: string;
  city?: string;
  country_id?: string;
  default_tax_code?: string | null;
  default_auto_generate_receipts?: boolean;
}

export interface UpdateServiceProviderResult {
  data: {
    name: string;
    mangopay_kyc_verified: boolean;
    mangopay_sca_enrolled: boolean;
    mangopay_sca_activated: boolean;
    mangopay_user_id: string | null;
    payout_schedule: 'disabled' | 'daily' | 'weekly';
    mangopay_payout_day_of_week: number | null;
    global_service_provider_id: string;
    legal_person_type: LegalPersonType;
    payment_service_provider: 'mangopay' | 'stripe' | null;
  };
  redirect_url?: string;
}

export interface IServiceProviderPhoenixApi extends Api {
  getServiceProvider(): Promise<GetServiceProviderResult>;
  getDashboard(): Promise<GetDashboardResult>;
  getPayouts(params: GetPayoutsParams): Promise<GetPayoutsResult>;
  getPayoutReconciliation(
    params: GetPayoutReconciliationParams
  ): Promise<GetPayoutReconciliationResult>;
  getOpenItems(): Promise<GetOpenItemsResult>;
  updateServiceProvider(dataIn: {
    body: { data: UpdateServiceProviderParams };
  }): Promise<UpdateServiceProviderResult>;
}

export class ServiceProviderPhoenixApi implements IServiceProviderPhoenixApi {
  baseUrl: string;
  headers: Headers;

  getServiceProvider: IServiceProviderPhoenixApi['getServiceProvider'];
  getDashboard: IServiceProviderPhoenixApi['getDashboard'];
  getPayouts: IServiceProviderPhoenixApi['getPayouts'];
  getPayoutReconciliation: IServiceProviderPhoenixApi['getPayoutReconciliation'];
  getOpenItems: IServiceProviderPhoenixApi['getOpenItems'];
  updateServiceProvider: IServiceProviderPhoenixApi['updateServiceProvider'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl('service-provider', apiUrl);
    this.headers = new Headers({
      'content-type': 'application/json',
      'x-pn-stripemode': 'live',
    });

    this.getServiceProvider = get({
      authContext,
      headers: this.headers,
      url: `${this.baseUrl}/profile`,
    });

    this.getDashboard = get({
      authContext,
      headers: this.headers,
      url: `${this.baseUrl}/mangopay/dashboard`,
    });

    this.getPayouts = get({
      authContext,
      headers: this.headers,
      url: (params) => {
        const query = new URLSearchParams();
        query.append('page', (params.page + 1).toString());
        query.append('page_size', params.pageSize.toString());

        if (params.state) {
          query.append(`filters[0][field]`, 'state');
          query.append(`filters[0][op]`, '==');
          query.append(`filters[0][value]`, params.state);
        }

        return `${this.baseUrl}/mangopay/payouts?${query.toString()}`;
      },
    });

    this.getPayoutReconciliation = get({
      authContext,
      headers: this.headers,
      url: ({ payoutId }: { payoutId: string }) =>
        `${this.baseUrl}/mangopay/payouts/${payoutId}/reconciliation`,
    });

    this.getOpenItems = get({
      authContext,
      headers: this.headers,
      url: `${this.baseUrl}/mangopay/open-items`,
    });

    this.updateServiceProvider = modify({
      authContext,
      method: 'PUT',
      headers: this.headers,
      url: `${this.baseUrl}/profile`,
    });
  }
}
